import { removeUnreadLabel } from '@/features/chats/model/conversation';
import {
   loadInitialMessages,
   loadMoreNewerMessages,
   loadMoreOlderMessages,
} from '@/features/chats/services/conversationService';
import { updateInviteStatus } from '@/features/invite/model/inviteSlice';
import { getInviteStatus } from '@/infrastructure/sdk/invite';
import { getUserIdFromJid } from '@/shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { getRecentChatDataById, recalculateUnreadMessageLabel } from '../model';
import { markChatLoadEnd, markChatLoadStart } from '../services/messageBuffer';

const EMPTY_ARRAY = [];

export function useConversationFetch(activeChatJid) {
   const dispatch = useDispatch();
   const store = useStore();
   const activeChatId = getUserIdFromJid(activeChatJid);

   const activeChat = getRecentChatDataById(activeChatId);

   const { messages, hasMoreOlder, hasMoreNewer } = useSelector(
      state => ({
         messages: state.conversationData.data[activeChatId]?.messages || EMPTY_ARRAY,
         hasMoreOlder: state.conversationData.data[activeChatId]?.hasMoreOlder ?? true,
         hasMoreNewer: state.conversationData.data[activeChatId]?.hasMoreNewer || false,
      }),
      shallowEqual,
   );
   const messagesLength = messages.length;

   const [loadingBottom, setLoadingBottom] = useState(false);
   const [loadingTop, setLoadingTop] = useState(false);
   const isFetchingRef = useRef(false);
   const processedChatJidRef = useRef(null);
   const activeChatIdRef = useRef(activeChatId);

   // ─── Effect: Load initial messages on chat switch ─────────────────────────
   // Only triggers when activeChatJid genuinely changes and store is empty.
   useEffect(() => {
      activeChatIdRef.current = activeChatId;
      if (processedChatJidRef.current === activeChatJid) return;
      processedChatJidRef.current = activeChatJid;

      if (!activeChatId) return;

      getInviteStatus(activeChatJid).then(response => {
         if (response?.statusCode === 200) {
            const { inviteStatus, remainingMessages, isSender } = response.data;
            dispatch(
               updateInviteStatus({
                  userId: activeChatId,
                  inviteStatus: inviteStatus,
                  remainingMessages,
                  isSender,
               }),
            );
         }
      });

      // ✅ Skip fetch if messages already exist for this chat in the store
      const existingMessages = store.getState().conversationData.data[activeChatId]?.messages;
      if (existingMessages?.length > 0) return;

      if (isFetchingRef.current) return;

      const lActiveChat = { ...(activeChat || {}), fromUserId: activeChatId };
      lActiveChat.chatType = activeChatJid.includes('@mix') ? 'groupChat' : 'chat';

      isFetchingRef.current = true;
      setLoadingTop(true);
      markChatLoadStart(activeChatId);
      loadInitialMessages(lActiveChat).finally(() => {
         isFetchingRef.current = false;
         setLoadingTop(false);
         markChatLoadEnd(activeChatId);
      });
   }, [activeChatJid]); // eslint-disable-line react-hooks/exhaustive-deps

   // ─── Effect: Recalculate unread on unmount ────────────────────────────────
   useEffect(() => {
      return () => {
         const chatId = activeChatIdRef.current;
         const chatData = getRecentChatDataById(chatId);
         if (!chatData || !chatData.unreadCount) {
            return dispatch(removeUnreadLabel({ chatId }));
         }
         dispatch(
            recalculateUnreadMessageLabel({
               chatId,
               unreadMessageId: chatData.unreadMessageId,
               unreadCount: chatData.unreadCount,
            }),
         );
      };
   }, [dispatch]);

   // ─── Helpers ──────────────────────────────────────────────────────────────

   const getAnchorId = useCallback(
      (fromEnd = false) => {
         // Exclude only UI-only labels — include both 'message' and 'groupedMedia'
         const actual = messages.filter(m => m.type !== 'date-label' && m.type !== 'unread-separator');
         if (!actual.length) return null;

         const msg = fromEnd ? actual[actual.length - 1] : actual[0];

         if (msg.type === 'groupedMedia' && Array.isArray(msg.messages) && msg.messages.length > 0) {
            return fromEnd ? msg.messages[0].msgId : msg.messages[msg.messages.length - 1].msgId;
         }

         return msg.msgId;
      },
      [messages],
   );

   // ─── Paginate older (scroll up) ───────────────────────────────────────────
   // Mapped to onLoadBottom in SmartListView (inverted list: bottom trigger fires at visual TOP = oldest end).
   // Also used by useAutoLoadMore to fill the screen when content is too short.
   const loadOlderMessages = useCallback(async () => {
      if (!hasMoreOlder || isFetchingRef.current) return;

      // If no messages yet, fall back to initial load
      if (!messagesLength) {
         const lActiveChat = { ...(activeChat || {}), fromUserId: activeChatId };
         lActiveChat.chatType = activeChatJid.includes('@mix') ? 'groupChat' : 'chat';

         isFetchingRef.current = true;
         setLoadingTop(true);
         try {
            await loadInitialMessages(lActiveChat);
         } finally {
            isFetchingRef.current = false;
            setLoadingTop(false);
         }
         return;
      }

      const anchorId = getAnchorId(true); // oldest visible message
      if (!anchorId) return;

      isFetchingRef.current = true;
      setLoadingTop(true);
      try {
         await loadMoreOlderMessages(activeChatJid, anchorId);
      } finally {
         isFetchingRef.current = false;
         setLoadingTop(false);
      }
   }, [hasMoreOlder, activeChatJid, activeChatId, activeChat, messagesLength, getAnchorId]);

   // ─── Paginate newer (scroll down) ────────────────────────────────────────
   // Mapped to onLoadTop in SmartListView (inverted list: top trigger fires at visual BOTTOM = newest end).
   const loadNewerMessages = useCallback(async () => {
      if (!hasMoreNewer || isFetchingRef.current || !messagesLength) return;
      const anchorId = getAnchorId(false); // newest visible message
      if (!anchorId) return;

      isFetchingRef.current = true;
      setLoadingBottom(true);
      try {
         await loadMoreNewerMessages(activeChatJid, anchorId);
      } finally {
         isFetchingRef.current = false;
         setLoadingBottom(false);
      }
   }, [hasMoreNewer, activeChatJid, messagesLength, getAnchorId]);

   return {
      messages,
      hasMoreOlder,
      hasMoreNewer,
      loadOlderMessages,
      loadNewerMessages,
      loadingTop,
      loadingBottom,
      activeChat,
   };
}
